<template>
  <!-- StoreEmbed：SSR 阶段展开为标准 TemplateNode 列表，走相同渲染链路（SEO 友好） -->
  <template v-if="node.component === 'StoreEmbed'">
    <template v-if="storeNodes.length > 0">
      <TemplateNodeRenderer
        v-for="(storeNode, index) in storeNodes"
        :key="storeNode.id || `store-${index}`"
        :node="storeNode"
        :selected-id="selectedId"
        :selection-version="selectionVersion"
        :disable-interaction="disableInteraction"
        :wrap-node="wrapNode"
        @select="handleChildSelect"
      />
    </template>
  </template>
  <template v-else-if="!isNodeHidden && resolvedComponent">
    <div
      v-if="wrapNode"
      v-node-marker="node.id"
      class="preview-node"
      :class="{ selected: isSelected }"
      @click.stop="handleClick"
      :data-node-id="node.id || null"
      :data-disable-interaction="disableInteraction ? 'true' : null"
    >
      <div class="preview-node-content">
        <component
          :is="resolvedComponent"
          :properties="mergedProperties"
          v-bind="selectionProps"
        >
          <template
            v-for="(children, slotName) in slotGroups"
            :key="slotName"
            v-slot:[slotName]
          >
            <TemplateNodeRenderer
              v-for="(child, index) in children"
              :key="child.id || `${child.component}-${index}`"
              :node="child"
              :selected-id="selectedId"
              :selection-version="selectionVersion"
              :disable-interaction="disableInteraction"
              :wrap-node="wrapNode"
              @select="handleChildSelect"
            />
          </template>
        </component>
      </div>
    </div>
    <component
      v-else
      :is="resolvedComponent"
      v-node-marker="node.id"
      :properties="mergedProperties"
      v-bind="selectionProps"
    >
      <template
        v-for="(children, slotName) in slotGroups"
        :key="slotName"
        v-slot:[slotName]
      >
        <TemplateNodeRenderer
          v-for="(child, index) in children"
          :key="child.id || `${child.component}-${index}`"
          :node="child"
          :selected-id="selectedId"
          :selection-version="selectionVersion"
          :disable-interaction="disableInteraction"
          :wrap-node="wrapNode"
          @select="handleChildSelect"
        />
      </template>
    </component>
  </template>
</template>

<script setup lang="ts">
import { computed, inject, provide, markRaw } from 'vue'
import type { TemplateJson, TemplateNode } from '~/utils/templateSdk'
import { buildRenderTree } from '~/utils/templateSdk'
import { getComponentDefinition, pickRuntimeDefinition } from '~/utils/remoteComponentRegistry'
import { resolveLocalComponent } from '~/utils/componentResolver'
import CmsGenericRenderer from '~/components/renderer/CmsGenericRenderer.vue'
import { withDomainHeaders } from '~/composable/useDomain'
import { buildLocalizedPath, normalizeLocale, parseLocalePath } from '~/utils/localeController'
import { applyStoreMenusToTemplate, normalizeStoreTemplateMenus, pickStoreTemplateMenus } from '~/utils/storeMenuLinks'

defineOptions({ name: 'TemplateNodeRenderer' })

const props = defineProps<{
  node: TemplateNode
  selectedId?: string | null
  selectionVersion?: number
  disableInteraction?: boolean
  wrapNode?: boolean
}>()

const emit = defineEmits<{
  (event: 'select', node: TemplateNode): void
}>()

// ---- StoreEmbed：SSR 阶段 fetch 店铺数据并展开为 TemplateNode 列表 ----
const isStoreEmbed = computed(() => props.node.component === 'StoreEmbed')

const requestHeaders = import.meta.server
  ? useRequestHeaders(['host', 'x-forwarded-host', 'x-forwarded-proto', 'lang', 'x-soho-route-lang', 'x-soho-route-path'])
  : {}
const route = useRoute()
const localeConfig = inject<any>('cmsLocaleConfig', null)
const pageRouteContext = inject<any>('cmsPageRouteContext', null)
const activeLocale = computed(() => normalizeLocale(
  localeConfig?.value?.activeLanguage
  || parseLocalePath(route.fullPath || '/').locale
  || requestHeaders['x-soho-route-lang']
  || requestHeaders.lang
  || 'en',
))
const requestRoutePath = computed(() => {
  const rawPath = String(pageRouteContext?.value?.browserPath || route.fullPath || '/').trim() || '/'
  return buildLocalizedPath(rawPath, activeLocale.value)
})
const requestHeadersWithLang = computed(() => ({
  ...requestHeaders,
  lang: activeLocale.value,
  'x-soho-route-lang': activeLocale.value,
  'x-soho-route-path': requestRoutePath.value,
}))
const currentFullUrl = computed(() => {
  if (import.meta.client) {
    return new URL(requestRoutePath.value, window.location.origin).href
  }
  const protocol = String(requestHeaders['x-forwarded-proto'] || 'https').split(',')[0].trim()
  const host = String(requestHeaders['x-forwarded-host'] || requestHeaders.host || '').split(',')[0].trim()
  return host ? `${protocol}://${host}${requestRoutePath.value}` : ''
})

type StoreTemplateResponse = {
  data?: unknown
  components?: unknown
  pageContent?: unknown
  menus?: unknown
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const parseStoreTemplateJson = (value: unknown): unknown => {
  if (typeof value !== 'string') return value
  if (!value.trim()) return null
  try {
    return JSON.parse(value)
  } catch {
    try {
      return JSON.parse(decodeURIComponent(value))
    } catch {
      return null
    }
  }
}

const pickStoreTemplatePayload = (value: unknown): TemplateJson | null => {
  const parsed = parseStoreTemplateJson(value)
  if (!parsed || typeof parsed !== 'object') return null
  const record = parsed as Record<string, unknown>

  if (Array.isArray(record.components)) return record as TemplateJson
  if (Array.isArray(parsed)) return parsed as TemplateJson
  if (typeof record.pageContent === 'string') return pickStoreTemplatePayload(record.pageContent)
  if (record.data) return pickStoreTemplatePayload(record.data)

  return record as TemplateJson
}

const buildStoreTemplateUrlCandidates = (fullUrl: string): string[] => {
  const urls = [fullUrl]
  try {
    const parsed = new URL(fullUrl)
    const localePath = parseLocalePath(`${parsed.pathname}${parsed.search}${parsed.hash}`)
    const match = localePath.canonicalPathname.match(/^(\/store-[^/]+)\//)
    if (match?.[1]) {
      for (const loc of ['home', 'products']) {
        const next = new URL(parsed.href)
        const canonicalPath = `${match[1]}/${loc}`
        next.pathname = localePath.locale
          ? buildLocalizedPath(canonicalPath, localePath.locale)
          : canonicalPath
        next.search = ''
        next.hash = ''
        urls.push(next.href)
      }
    }
  } catch {
    // keep original candidate only
  }
  return Array.from(new Set(urls))
}

type StoreEmbedTemplateData = {
  payload: TemplateJson | null
  menus: unknown[]
}

const emptyStoreEmbedTemplateData = (): StoreEmbedTemplateData => ({
  payload: null,
  menus: [],
})

const storeEmbedAsyncDataKey = computed(() => [
  'store-embed',
  props.node.id || 'root',
  activeLocale.value,
  requestRoutePath.value || '/',
].join(':'))

const { data: storeTemplateData } = await useAsyncData<StoreEmbedTemplateData>(
  () => storeEmbedAsyncDataKey.value,
  async () => {
    if (!isStoreEmbed.value || !currentFullUrl.value) return emptyStoreEmbedTemplateData()
    for (const url of buildStoreTemplateUrlCandidates(currentFullUrl.value)) {
      try {
        const resp = await $fetch<StoreTemplateResponse>('/api/biz/store/template', {
          method: 'GET',
          query: { url },
          headers: import.meta.server
            ? withDomainHeaders(requestHeadersWithLang.value)
            : withDomainHeaders(),
        })
        const payload = pickStoreTemplatePayload(resp)
        if (payload) {
          const menus = normalizeStoreTemplateMenus(pickStoreTemplateMenus(resp), {
            currentUrl: url,
            currentPath: requestRoutePath.value,
          })
          return { payload, menus }
        }
      } catch (error) {
        console.warn('[TemplateNodeRenderer] StoreEmbed template candidate failed:', url, error)
      }
    }
    return emptyStoreEmbedTemplateData()
  },
  {
    server: true,
    lazy: false,
    default: emptyStoreEmbedTemplateData,
  },
)

const storeNodes = computed<TemplateNode[]>(() => {
  if (!storeTemplateData.value?.payload) return []
  const template = applyStoreMenusToTemplate(storeTemplateData.value.payload, storeTemplateData.value.menus)
  return buildRenderTree(template)
})

provide('previewNodeId', computed(() => props.node.id ?? null))

type RegisterNode = (id: string, el: HTMLElement) => void
const registerNode = inject<RegisterNode | null>('previewNodeRegister', null)
const unregisterNode = inject<RegisterNode | null>('previewNodeUnregister', null)

// 使用 Nuxt 状态管理当前页面被收集的组件定义（仅在 SSR 时记录并传给客户端）
const ssrUsedDefinitions = useState<Record<string, any>>('cmsUsedDefinitions', () => ({}))

const resolvedComponent = computed(() => {
  const name = props.node.component
  // 1. 优先使用 Schema 定义（统一 HTML+JS 渲染路径）
  const schemaDef = getComponentDefinition(name)
  if (schemaDef) {
    if (import.meta.server) {
      // 在服务端运行时收集该渲染的定义，并剔除编辑器相关的冗余字段，只注水渲染所需的核心数据
      ssrUsedDefinitions.value[name] = pickRuntimeDefinition(schemaDef)
    }
    return markRaw(CmsGenericRenderer)
  }
  // 2. 回退到本地 .vue 组件（通过统一的 componentResolver）
  const local = resolveLocalComponent(name)
  if (local) return local
  // 3. 未知组件不走 resolveComponent，避免 Vite 尝试加载不存在的 .vue 文件
  console.warn(`[TemplateNodeRenderer] 未知组件: ${name}，已跳过渲染`)
  return null
})

/** 当使用 CmsGenericRenderer 时，需要传递 definition prop */
const genericRendererProps = computed(() => {
  const name = props.node.component
  const schemaDef = getComponentDefinition(name)
  if (schemaDef) return { definition: schemaDef }
  return {}
})

const mergedProperties = computed(() => {
  return (props.node.properties ?? {}) as Record<string, unknown>
})

const slotGroups = computed<Record<string, TemplateNode[]>>(() => {
  const groups: Record<string, TemplateNode[]> = {}
  const children = props.node.body || []
  children.forEach((child) => {
    const slotName = child.slot || 'default'
    if (!groups[slotName]) groups[slotName] = []
    groups[slotName].push(child)
  })
  return groups
})

const isSelected = computed(() => !!props.selectedId && props.selectedId === props.node.id)
const wrapNode = computed(() => props.wrapNode !== false)
const isNodeHidden = computed(() => props.node.properties?.visibility === 'hide')

const vNodeMarker = {
  mounted(el: HTMLElement, binding: { value?: string | null }) {
    const id = binding.value || null
    if (!id) return
    ;(el as { __previewNodeId?: string }).__previewNodeId = id
    registerNode?.(id, el)
  },
  updated(el: HTMLElement, binding: { value?: string | null }) {
    const nextId = binding.value || null
    const currentId = (el as { __previewNodeId?: string }).__previewNodeId || null
    if (currentId && currentId !== nextId) {
      unregisterNode?.(currentId, el)
    }
    if (nextId && nextId !== currentId) {
      ;(el as { __previewNodeId?: string }).__previewNodeId = nextId
      registerNode?.(nextId, el)
    }
  },
  unmounted(el: HTMLElement) {
    const currentId = (el as { __previewNodeId?: string }).__previewNodeId
    if (currentId) {
      unregisterNode?.(currentId, el)
    }
    delete (el as { __previewNodeId?: string }).__previewNodeId
  },
}
const selectionProps = computed(() => {
  const extra: Record<string, unknown> = {}
  if (props.node.component === 'AnnouncementGroup') {
    extra.selectedId = props.selectedId
    extra.selectionVersion = props.selectionVersion
  }
  const grProps = genericRendererProps.value
  if (grProps.definition) {
    extra.definition = grProps.definition
  }
  return extra
})

const handleClick = () => {
  emit('select', props.node)
}

const handleChildSelect = (node: TemplateNode) => {
  emit('select', node)
}

</script>

<style scoped>
.preview-node {
  position: relative;
}

.preview-node.selected {
  position: relative;
  z-index: 2;
}

.preview-node-content.selected {
  position: relative;
  z-index: 2;
}

.preview-node[data-disable-interaction='true'] .preview-node-content :deep(a),
.preview-node[data-disable-interaction='true'] .preview-node-content :deep(button),
.preview-node[data-disable-interaction='true'] .preview-node-content :deep(input),
.preview-node[data-disable-interaction='true'] .preview-node-content :deep(select),
.preview-node[data-disable-interaction='true'] .preview-node-content :deep(textarea) {
  pointer-events: none;
}

.preview-node-content[data-disable-interaction='true'] :deep(a),
.preview-node-content[data-disable-interaction='true'] :deep(button),
.preview-node-content[data-disable-interaction='true'] :deep(input),
.preview-node-content[data-disable-interaction='true'] :deep(select),
.preview-node-content[data-disable-interaction='true'] :deep(textarea) {
  pointer-events: none;
}
</style>
